import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Column, DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  PartnerProviderLog,
  PartnerProviderLogsFilters,
  PartnerProviderLogsMeta,
} from "@/types/partner-provider-log";
import { formatDateTime } from "@/utils/dateUtils";
import { cn } from "@/lib/utils";
import { ChevronDown, Download, FileSpreadsheet, FileText } from "lucide-react";
import {
  formatDurationMs,
  getPartnerProviderLogStatusKey,
  getPartnerProviderState,
  isPartialPartnerProviderLog,
} from "../utils/partnerProviderLogs";
import { PartnerProviderLogDetailsDialog } from "./PartnerProviderLogDetailsDialog";
import { PartnerProviderLogsFiltersPopover } from "./PartnerProviderLogsFiltersPopover";

interface PartnerProviderLogsTableProps {
  isLoading: boolean;
  isExporting: boolean;
  logs: PartnerProviderLog[];
  meta: PartnerProviderLogsMeta | null;
  filters: PartnerProviderLogsFilters;
  onFiltersChange: (filters: PartnerProviderLogsFilters) => void;
  onExport: (format: "csv" | "excel") => void;
}

const logStatusBadgeVariant = {
  healthy: "verified",
  error: "new_destructive",
  apiError: "new_destructive",
  syncing: "new_default",
  triggered: "new_secondary",
  unknown: "secondary",
} as const;

export const PartnerProviderLogsTable: React.FC<
  PartnerProviderLogsTableProps
> = ({
  isLoading,
  isExporting,
  logs,
  meta,
  filters,
  onFiltersChange,
  onExport,
}) => {
  const { t } = useTranslation();
  const [selectedLog, setSelectedLog] = useState<PartnerProviderLog | null>(
    null,
  );

  const totalPages = meta
    ? Math.max(1, Math.ceil(meta.total / Math.max(meta.limit, 1)))
    : 1;

  const columns: Column<PartnerProviderLog>[] = [
    {
      key: "createdAt",
      header: t("partners.admin.logColumns.time"),
      render: (value) => (
        <span className="text-sm">{formatDateTime(value)}</span>
      ),
    },
    {
      key: "provider",
      header: t("partners.admin.logColumns.provider"),
    },
    {
      key: "eventType",
      header: t("partners.admin.logColumns.event"),
      render: (value) =>
        t(`partners.admin.eventTypeValues.${value}`, { defaultValue: value }),
    },
    {
      key: "operation",
      header: t("partners.admin.logColumns.operation"),
      render: (value) => value || "-",
    },
    {
      key: "status",
      header: t("partners.admin.logColumns.status"),
      render: (_, row) => {
        const state = getPartnerProviderState(row);

        return (
          <Badge
            variant={
              isPartialPartnerProviderLog(row)
                ? "outline"
                : logStatusBadgeVariant[state]
            }
            className={cn(
              isPartialPartnerProviderLog(row) &&
                "border-amber-500 text-amber-700",
            )}
          >
            {t(
              `partners.admin.logStatusValues.${getPartnerProviderLogStatusKey(row)}`,
              { defaultValue: row.status },
            )}
          </Badge>
        );
      },
    },
    {
      key: "message",
      header: t("partners.admin.logColumns.message"),
      className: "max-w-[280px]",
      render: (value) => (
        <span className="block truncate text-sm" title={value || ""}>
          {value || "-"}
        </span>
      ),
    },
    {
      key: "durationMs",
      header: t("partners.admin.logColumns.duration"),
      render: (value) => formatDurationMs(value),
    },
    {
      key: "httpStatus",
      header: t("partners.admin.logColumns.httpStatus"),
      render: (value) => value ?? "-",
    },
  ];

  return (
    <>
      <div className="flex flex-col gap-4 rounded-lg border p-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-lg font-semibold">
            {t("partners.admin.logsTitle")}
          </h3>
          <div className="flex items-center gap-3">
            {meta && (
              <span className="text-sm text-muted-foreground">
                {t("partners.admin.logsTotal", { count: meta.total })}
              </span>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isLoading || isExporting}
                >
                  <Download className="size-4" />
                  {t("partners.admin.export.button")}
                  <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onExport("csv")}>
                  <FileText className="size-4" />
                  {t("partners.admin.export.csv")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onExport("excel")}>
                  <FileSpreadsheet className="size-4" />
                  {t("partners.admin.export.excel")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <PartnerProviderLogsFiltersPopover
              filters={filters}
              onFiltersChange={onFiltersChange}
            />
          </div>
        </div>

        <DataTable<PartnerProviderLog>
          data={logs}
          columns={columns}
          loading={isLoading}
          withSorting={false}
          emptyMessage={t("partners.noLogs")}
          pagination={{
            currentPage: meta?.page ?? filters.page ?? 1,
            totalPages,
            onPageChange: (page) => onFiltersChange({ ...filters, page }),
          }}
          onRowClick={(row) => setSelectedLog(row)}
          className={cn(isLoading && "pointer-events-none")}
        />
      </div>

      <PartnerProviderLogDetailsDialog
        log={selectedLog}
        open={selectedLog !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedLog(null);
        }}
      />
    </>
  );
};
